// Frame composition (RALLY_GOAL.md §11, §14): scene pass at a dynamic resolution scale, optional
// GTAO (High), optional camera motion blur and depth of field (Ultra, P1-4), filmic tone mapping +
// sRGB, then FXAA at full resolution. One shader path for the WebGPU and WebGL2 backends.
import * as THREE from 'three/webgpu';
import { pass, renderOutput, vec4, vec2, mix, float, uniform, uv, int, clamp, length, smoothstep, getViewPosition } from 'three/tsl';
import { ao } from 'three/addons/tsl/display/GTAONode.js';
import { denoise } from 'three/addons/tsl/display/DenoiseNode.js';
import { fxaa } from 'three/addons/tsl/display/FXAANode.js';
import { motionBlur } from 'three/addons/tsl/display/MotionBlur.js';
import { dof } from 'three/addons/tsl/display/DepthOfFieldNode.js';
import { fxaaMapped } from './fxaa';

export interface PipelineOptions {
  ao: boolean;
  scale: number;
  motionBlur?: boolean;
  dof?: boolean;
}

/** Longest blur streak (screen fraction) and how much of the camera's motion over a frame it shows. */
const MAX_STREAK = 0.035;
const SHUTTER = 0.5;
/** Bokeh scale below which depth of field is invisible (of 1.6 on the showcase): the frame skips it. */
const DOF_MIN = 0.02;

export class FramePipeline {
  private pipeline: THREE.RenderPipeline;
  /** The same frame without depth of field (Ultra): its passes (circle of confusion, blurs, composite:
   * 10 of the 17 render passes in an Ultra frame) only run while the bokeh is visible, on the menu
   * and results showcase. Every pass leaves WebGPU objects for the garbage collector, and with them
   * Ultra kept V8's young generation at its 32 MB maximum. */
  private plainPipeline: THREE.RenderPipeline | null = null;
  private warmed = false;
  private scenePass: ReturnType<typeof pass>;
  readonly aoEnabled: boolean;
  readonly motionBlurEnabled: boolean;
  readonly dofEnabled: boolean;
  private readonly camera: THREE.Camera;
  private readonly prevViewProj = uniform(new THREE.Matrix4());
  private readonly currViewProj = new THREE.Matrix4();
  private hasPrev = false;
  /** Motion-blur strength (0 = off, e.g. on the first frame after a camera cut). */
  private readonly blurAmount = uniform(0);
  /** Depth of field: focus distance (m) and bokeh scale (0 = sharp). */
  readonly focus = uniform(6);
  readonly bokeh = uniform(0);

  constructor(renderer: THREE.WebGPURenderer, scene: THREE.Scene, camera: THREE.Camera, opts: PipelineOptions) {
    this.aoEnabled = opts.ao;
    this.motionBlurEnabled = !!opts.motionBlur;
    this.dofEnabled = !!opts.dof;
    this.camera = camera;
    this.pipeline = new THREE.RenderPipeline(renderer);
    this.pipeline.outputColorTransform = false;
    const sp = pass(scene, camera);
    this.scenePass = sp;
    let color = sp.getTextureNode('output') as unknown as ReturnType<typeof vec4>;
    if (this.motionBlurEnabled) {
      // Camera motion blur: each pixel's world position (from depth) reprojected with last frame's
      // view-projection gives its screen motion. Faded out within ~10 m, so the player's car and
      // the cockpit stay sharp (they move with the camera).
      const depth = sp.getTextureNode('depth').r;
      const viewPos = getViewPosition(uv(), depth, uniform(camera.projectionMatrixInverse));
      const world = uniform(camera.matrixWorld).mul(vec4(viewPos, 1));
      const prev = this.prevViewProj.mul(world);
      const prevUv = prev.xy.div(prev.w).mul(vec2(0.5, -0.5)).add(0.5);
      let vel = uv().sub(prevUv).mul(this.blurAmount);
      const len = length(vel);
      vel = vel.mul(clamp(float(MAX_STREAK).div(len.max(1e-5)), 0, 1)).mul(smoothstep(8, 16, viewPos.z.negate()));
      color = motionBlur(sp.getTextureNode('output'), vel, int(8)) as unknown as ReturnType<typeof vec4>;
    }
    if (opts.ao) {
      // Depth-only GTAO (normals reconstructed from depth): an MRT normal buffer would carry the
      // foliage cards' "volume" normals and the particle quads, both of which smear dark blocks.
      const aoPass = ao(sp.getTextureNode('depth'), null as unknown as THREE.Node, camera);
      aoPass.resolutionScale = 0.5;
      aoPass.radius.value = 0.6;
      aoPass.thickness.value = 0.6;
      aoPass.samples.value = 12;
      // Half-resolution GTAO is noisy: denoise it (normals from depth again), or the grain shows
      // through the glass and along backlit foliage.
      const occl = (denoise(aoPass.getTextureNode(), sp.getTextureNode('depth'), null as unknown as THREE.Node, camera) as unknown as ReturnType<typeof vec4>).r;
      // Contact occlusion, kept subtle so sunlit ground doesn't go muddy.
      const k = mix(float(1), occl, float(0.5));
      color = color.mul(vec4(k, k, k, float(1))) as unknown as typeof color;
    }
    if (this.dofEnabled) {
      this.plainPipeline = new THREE.RenderPipeline(renderer);
      this.plainPipeline.outputColorTransform = false;
      this.plainPipeline.outputNode = fxaa(renderOutput(color));
      // Showcase depth of field (menu and results): the car sharp, the forest behind soft.
      color = dof(color, sp.getViewZNode(), this.focus, float(9), this.bokeh) as unknown as ReturnType<typeof vec4>;
    }
    // Low/Medium (the scene texture straight to the output): FXAA tone-maps as it samples, one pass
    // instead of two. With AO, motion blur or depth of field the colour is an expression and needs
    // its own pass anyway.
    const plain = !opts.ao && !this.motionBlurEnabled && !this.dofEnabled;
    this.pipeline.outputNode = plain ? fxaaMapped(sp.getTextureNode('output') as unknown as THREE.TextureNode, (c) => renderOutput(c as never) as never) : fxaa(renderOutput(color));
    this.setScale(opts.scale);
  }

  setScale(s: number): void {
    this.scenePass.setResolutionScale(s);
  }

  /** `blur`: motion-blur strength this frame (0 after a camera cut or when paused). */
  render(blur = 1): void {
    const cam = this.camera;
    if (this.motionBlurEnabled) {
      cam.updateMatrixWorld();
      this.currViewProj.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      this.blurAmount.value = this.hasPrev ? blur * SHUTTER : 0;
    }
    const plain = this.plainPipeline;
    const p = plain && this.bokeh.value < DOF_MIN ? plain : this.pipeline;
    if (plain && !this.warmed) {
      // Build the other variant once now, so switching at the start line doesn't compile shaders.
      (p === plain ? this.pipeline : plain).render();
      this.warmed = true;
    }
    p.render();
    if (this.motionBlurEnabled) {
      this.prevViewProj.value.copy(this.currViewProj);
      this.hasPrev = true;
    }
  }

  /** Forget the previous camera (teleports, camera switches): no streak across the cut. */
  cut(): void {
    this.hasPrev = false;
  }

  dispose(): void {
    this.pipeline.dispose();
    this.plainPipeline?.dispose();
  }
}
